import { useCallback, useState } from 'react';
import { useSSEConnection } from './useSSEConnection';
import type { UseAdminAppReturn } from './useAdminApp';

export interface EntitySSEConfig<TEntity, TEvent> {
  adminApp: UseAdminAppReturn;
  streamPath: string; // e.g., 'tags', 'tags/assignments'
  onConnected?: (data: { type: 'connected'; [key: string]: unknown }) => void;
  onEvent?: (event: TEvent) => void;
  enabled?: boolean;
  includeInitial?: boolean;
}

export interface EntitySSEReturn {
  enabled: boolean;
  setEnabled: (enabled: boolean) => void;
  includeInitial: boolean;
  setIncludeInitial: (include: boolean) => void;
}

/**
 * Generic SSE subscription hook for entity streams
 * Handles connection state and provides toggle controls
 * 
 * @example
 * // In useParticipantAdmin.ts:
 * const participantSSE = useEntitySSE<Participant, ParticipantEvent>({
 *   adminApp,
 *   streamPath: 'participants',
 *   onConnected: (data) => {
 *     if (data.participants) setEntities(data.participants);
 *   },
 *   onEvent: (event) => {
 *     if (event.action === 'created') setEntities(prev => [...prev, event.participant]);
 *   },
 * });
 * 
 * // Then expose in return:
 * return {
 *   ...baseHandler,
 *   sseEnabled: participantSSE.enabled,
 *   setSseEnabled: participantSSE.setEnabled,
 *   includeInitialData: participantSSE.includeInitial,
 *   setIncludeInitialData: participantSSE.setIncludeInitial,
 * };
 */
export function useEntitySSE<TEntity = unknown, TEvent = unknown>({
  adminApp,
  streamPath,
  onConnected,
  onEvent,
  enabled: initialEnabled = false,
  includeInitial: initialIncludeInitial = false,
}: EntitySSEConfig<TEntity, TEvent>): EntitySSEReturn {
  const [enabled, setEnabled] = useState(initialEnabled);
  const [includeInitial, setIncludeInitial] = useState(initialIncludeInitial);

  // Memoized message handler
  const handleMessage = useCallback((event: MessageEvent) => {
    try {
      const data = JSON.parse(event.data);
      
      // Handle initial connection data
      if (data.type === 'connected') {
        onConnected?.(data);
        return;
      }
      
      // Handle real-time events
      onEvent?.(data);
    } catch (error) {
      console.error('[Entity SSE] Error processing message:', error);
    }
  }, [onConnected, onEvent]);

  const handleError = useCallback((error: Event) => {
    console.error('[Entity SSE] Connection error:', error);
  }, []);

  const handleOpen = useCallback(() => {
    console.log('[Entity SSE] Connected to stream:', streamPath);
  }, [streamPath]);

  // Establish SSE connection
  useSSEConnection({
    url: `${adminApp.httpUrl}/stream/${streamPath}/${adminApp.workspaceId}?includeInitial=${includeInitial}`,
    enabled,
    onMessage: handleMessage,
    onError: handleError,
    onOpen: handleOpen,
  });

  return {
    enabled,
    setEnabled,
    includeInitial,
    setIncludeInitial,
  };
}

